import { useState, useEffect, useRef, RefObject } from 'react';

interface UseLazySectionOptions {
  rootMargin?: string;
  threshold?: number;
  triggerOnce?: boolean;
}

export function useLazySection(options: UseLazySectionOptions = {}): [RefObject<HTMLDivElement>, boolean] {
  const { 
    rootMargin = '200px', // Start loading 200px before visible
    threshold = 0,
    triggerOnce = true 
  } = options;
  
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // If already visible and triggerOnce, skip observer setup
    if (isVisible && triggerOnce) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [rootMargin, threshold, triggerOnce, isVisible]);

  return [ref, isVisible];
}
